/**
 * Server state for build step 8 — opportunities (packages/contracts documents.ts, routes.step8 `opportunities.*`):
 *   GET /opportunities (the board: every card, newest first, plus the three counts — the server scopes a member to
 *   the clients they are SPOC of and leaves `value_lakh` out for anyone without revenue access),
 *   GET /opportunities/{id} (the drawer), POST /opportunities ("Add an opportunity", starts in Spotted),
 *   POST /opportunities/{id}/stage ("Move to"), PATCH /opportunities/{id} (managers set the value).
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { OPP_STAGE_LABEL, OppStage, type Opportunity, type OppType } from '@amplispot/contracts';
import { ApiFailure, call } from '../api';
import { refreshAfterWrite } from './work';

export const okeys = {
  all: ['opportunities'] as const,
  board: (clientId: string | null) => ['opportunities', 'board', clientId ?? 'all'] as const,
  one: (id: string) => ['opportunities', 'one', id] as const,
};

/** Prototype OSTAGES, in board order. */
export const OPP_STAGES = OppStage.options.map((k) => [k, OPP_STAGE_LABEL[k]] as const);

/** Prototype card chip: Upsell hot / Referral warm / Cross-sell info. */
export const oppTypeChip = (t: OppType) => (t === 'Upsell' ? 'hot' : t === 'Referral' ? 'warm' : 'info');

/**
 * The estimated value as the prototype writes it ("₹2.0 L / mo", "Unknown"). `null` when the caller may not see
 * value (the field is absent for members) — then nothing is rendered at all.
 */
export function oppValue(o: Pick<Opportunity, 'value_unknown' | 'value_lakh'>): string | null {
  if (o.value_lakh === undefined) return null;
  if (o.value_lakh === null || o.value_unknown) return 'Unknown';
  const v = o.value_lakh;
  if (v >= 100) return `₹${(v / 100).toFixed(2).replace(/0$/, '')} Cr / mo`;
  // One decimal as the prototype writes it (₹2.0, ₹0.8); keep a second only when it was entered (₹0.25).
  const oneDp = Math.abs(v * 10 - Math.round(v * 10)) < 1e-9;
  return `₹${oneDp ? v.toFixed(1) : String(Number(v.toFixed(2)))} L / mo`;
}

export function refreshOpps(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: okeys.all });
  refreshAfterWrite(qc);
}

// ---------------------------------------------------------------- reads

const PAGE = 200;

/** GET /opportunities, every page — a kanban shows every card. The counts come with the first page. */
export function useOpportunities(clientId: string | null = null) {
  return useQuery({
    queryKey: okeys.board(clientId),
    queryFn: async () => {
      const first = await call('opportunities.list', { query: { limit: PAGE, client_id: clientId ?? undefined } });
      const data: Opportunity[] = [...first.data];
      let cursor = first.next_cursor;
      while (cursor) {
        const next = await call('opportunities.list', { query: { limit: PAGE, cursor, client_id: clientId ?? undefined } });
        data.push(...next.data);
        cursor = next.next_cursor;
      }
      return { data, stats: first.stats };
    },
  });
}

/**
 * Prototype `xsell(c)`: per client, the opportunities not yet closed — the "N cross-sell" chip on a client box
 * and Manager Today card, and the "N in progress" cell in the client table. Built from the same board query
 * (GET /opportunities, every page), so it shares that cache. A client with none is absent from the map.
 */
export function useCrossSellCounts() {
  const opps = useOpportunities(null);
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of opps.data?.data ?? []) if (o.stage !== 'closed') m.set(o.client.id, (m.get(o.client.id) ?? 0) + 1);
    return m;
  }, [opps.data]);
  return { counts, query: opps };
}

export function useOpportunity(id: string | null) {
  return useQuery({
    queryKey: okeys.one(id ?? ''),
    queryFn: () => call('opportunities.get', { params: { id: id! } }) as Promise<Opportunity>,
    enabled: !!id,
  });
}

// ---------------------------------------------------------------- writes

/** A write that refreshes the board; a 409/412 (someone else moved it) refreshes too, so the drawer is current. */
export function useOppWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => refreshOpps(qc),
    onError: (e) => {
      if (e instanceof ApiFailure && (e.status === 409 || e.status === 412)) refreshOpps(qc);
    },
  });
}

export type { Opportunity };
